import React from 'react';
import type {
  SecretFlag,
  SecretPatternLabel,
} from '@volley/crucible-ipc-types/diagnostics-secret-scan';
import { summariseSecretFlags } from '@volley/crucible-ipc-types/diagnostics-secret-scan';

/**
 * Banner summarising token-shaped substrings found in a diagnostic
 * bundle's rendered text. Renders above the `<BundlePreview />` pane
 * in Settings → Diagnostics.
 *
 * Cloud-VM supportability TDD §C-1 "Renderer UX (MVP — no per-line ack)".
 * The Copy gate keys off `reviewedFlags` (set by the parent when the
 * user ticks the "I've reviewed" checkbox). Per-line ack is deferred
 * to a C-3 follow-up.
 */

export interface SecretFlagBannerProps {
  flags: ReadonlyArray<SecretFlag>;
}

const PATTERN_LABEL_COPY: Record<SecretPatternLabel, string> = {
  'github-pat-ghp': 'GitHub PAT (ghp_)',
  'github-pat-gho': 'GitHub PAT (gho_)',
  'github-pat-ghs': 'GitHub PAT (ghs_)',
  'github-pat-ghu': 'GitHub PAT (ghu_)',
  'github-pat-ghr': 'GitHub PAT (ghr_)',
  'github-pat-fine-grained': 'GitHub fine-grained PAT',
  'anthropic-key': 'Anthropic API key',
  'aws-key': 'AWS access key ID',
  'aws-secret-key': 'AWS secret access key',
  'datadog-api-key': 'Datadog API key',
  'datadog-app-key': 'Datadog app key',
  'npm-token': 'npm access token',
  'http-bearer': 'HTTP Bearer token',
  'credentialed-url': 'URL with embedded credentials',
  'high-entropy': 'High-entropy string (possible secret)',
};

export function SecretFlagBanner(props: SecretFlagBannerProps): React.JSX.Element | null {
  if (props.flags.length === 0) return null;
  const summary = summariseSecretFlags([...props.flags]);
  return (
    <div
      role="alert"
      data-testid="secret-flag-banner"
      className="rounded-md border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-xs text-rose-200"
    >
      <p className="font-medium">
        {props.flags.length === 1
          ? '1 token-shaped substring detected — please review before copying.'
          : `${props.flags.length} token-shaped substrings detected — please review before copying.`}
      </p>
      <ul className="mt-1 space-y-0.5 text-rose-200/80">
        {summary.map((entry) => (
          <li key={entry.label}>
            {entry.count}× {PATTERN_LABEL_COPY[entry.label]}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-rose-200/70">
        The diagnostics text has already been redacted once. Anything still flagged is either a new
        pattern not yet covered or a high-entropy fallback hit.
      </p>
    </div>
  );
}
